/**
 * Console primitives (kept here, not in @becca/ui, which the guest builder owns). Light console theme
 * from `[data-becca-theme="console"]`; every control has a visible focus ring.
 */
import { Link, type LinkProps } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { errorMessage } from '../lib/errors';
import { isPlaceholder, PLACEHOLDER_PREFIX } from '../lib/format';

export const focusRing =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

const variants = {
  primary: 'bg-primary text-on-primary hover:opacity-90',
  secondary: 'border border-line bg-surface text-fg hover:bg-bg',
  ghost: 'text-fg hover:bg-bg',
  danger: 'border border-red-700/40 bg-surface text-red-800 hover:bg-red-50',
} as const;

export function buttonClass(variant: keyof typeof variants = 'secondary', size: 'sm' | 'md' = 'md') {
  return [
    'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
    size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm',
    variants[variant],
    focusRing,
  ].join(' ');
}

export function Button({
  variant = 'secondary',
  size = 'md',
  busy = false,
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof variants;
  size?: 'sm' | 'md';
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || busy}
      className={`${buttonClass(variant, size)} ${className}`}
    >
      {busy && <Loader2 aria-hidden className="size-4 animate-spin" />}
      {children}
    </button>
  );
}

export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  className = '',
  ...props
}: LinkProps & { variant?: keyof typeof variants; size?: 'sm' | 'md'; className?: string; children?: ReactNode }) {
  return <Link {...props} className={`${buttonClass(variant, size)} ${className}`} />;
}

export function Card({ title, actions, children, className = '' }: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-lg border border-line bg-surface ${className}`}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          {title && <h2 className="becca-heading text-sm">{title}</h2>}
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="becca-heading text-xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

const tones = {
  neutral: 'border-line text-muted',
  accent: 'border-accent/40 text-accent',
  warn: 'border-amber-600/50 bg-amber-50 text-amber-900',
  danger: 'border-red-700/40 bg-red-50 text-red-800',
  ok: 'border-emerald-700/40 bg-emerald-50 text-emerald-900',
} as const;

export function Badge({ tone = 'neutral', children }: { tone?: keyof typeof tones; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

/** Seed content Daniel has not confirmed yet (08 §8). Shown wherever the row appears. */
export function PlaceholderFlag({ text }: { text?: string | null }) {
  if (text !== undefined && !isPlaceholder(text)) return null;
  return <Badge tone="warn">{PLACEHOLDER_PREFIX} confirm</Badge>;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <p role="status" className="flex items-center gap-2 p-4 text-sm text-muted">
      <Loader2 aria-hidden className="size-4 animate-spin" /> {label}
    </p>
  );
}

export function ErrorNotice({ error, title }: { error: unknown; title?: string }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-md border border-red-700/40 bg-red-50 px-3 py-2 text-sm text-red-900">
      {title && <strong className="mr-1">{title}</strong>}
      {errorMessage(error)}
    </div>
  );
}

export function Notice({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'ok' | 'warn' }) {
  const cls = {
    neutral: 'border-line bg-bg text-muted',
    ok: 'border-emerald-700/40 bg-emerald-50 text-emerald-900',
    warn: 'border-amber-600/50 bg-amber-50 text-amber-900',
  }[tone];
  return (
    <div role="status" className={`rounded-md border px-3 py-2 text-sm ${cls}`}>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="p-4 text-center text-sm text-muted">{children}</p>;
}

/** A phase-0 stand-in for a later feature: labelled, visible, inert. */
export function FuturePlaceholder({ label, phase }: { label: string; phase: string }) {
  return (
    <button
      type="button"
      disabled
      title={`Arrives in ${phase}`}
      className="inline-flex cursor-not-allowed items-center gap-1 rounded-md border border-dashed border-line px-3 py-2 text-sm text-muted"
    >
      {label} <span className="text-xs">· PLACEHOLDER — {phase}</span>
    </button>
  );
}

export function ReadOnlyNotice() {
  return <Notice>Read-only: your role can view this but not change it.</Notice>;
}
